import React, { useState, useEffect, useRef, useCallback } from "react";
import { searchMedicines } from "../../api/apiService";

const dropdownStyles = {
  container: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 1000,
    background: "#fff",
    border: "1px solid #d1d5db",
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
    maxHeight: "220px",
    overflowY: "auto",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  },
  item: {
    padding: "9px 14px",
    cursor: "pointer",
    fontSize: "14px",
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "background-color 0.15s",
  },
  code: {
    color: "#9ca3af",
    fontSize: "11px",
    fontFamily: "monospace",
    marginLeft: "12px",
    flexShrink: 0,
  },
  message: {
    padding: "10px 14px",
    color: "#6b7280",
    fontSize: "13px",
  },
  selectedContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "42px",
    background: "#f0f7ff",
    border: "1px solid #93b4e8",
    borderRadius: "8px",
    padding: "0 10px 0 40px",
    boxSizing: "border-box",
    gap: "8px",
  },
  selectedText: {
    flex: 1,
    fontSize: "14px",
    fontWeight: 500,
    color: "#111928",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  snomedBadge: {
    fontSize: "11px",
    fontFamily: "monospace",
    color: "#4b72b8",
    background: "#dbeafe",
    borderRadius: "4px",
    padding: "2px 6px",
    flexShrink: 0,
  },
  clearBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#6b7280",
    fontSize: "18px",
    lineHeight: 1,
    padding: "0 2px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
};

const MedicineAutocomplete = ({
  drugName,
  snomedCode,
  snomedDisplay,
  onSelect,
  onClear,
  onChange,
  placeholder,
  compact = false,
  disabled = false,
}) => {
  const [searchText, setSearchText] = useState(drugName || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const isSelected = Boolean(snomedCode);

  // Sync searchText when incoming drugName prop changes and not currently in SNOMED selected state
  useEffect(() => {
    if (!isSelected) {
      setSearchText(drugName || "");
    }
  }, [drugName, isSelected]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      setNoResults(false);
      return;
    }

    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setShowDropdown(true);
    setNoResults(false);
    setActiveIndex(-1);

    try {
      const response = await searchMedicines(query, 10);

      // If this request was aborted, don't update state
      if (controller.signal.aborted) return;

      const data = response?.data || [];
      setSuggestions(data);
      setNoResults(data.length === 0);
    } catch (err) {
      if (controller.signal.aborted) return;
      // Silently hide dropdown on error
      setSuggestions([]);
      setShowDropdown(false);
      setNoResults(false);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (onChange) {
      onChange(val);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      setNoResults(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(val.trim());
    }, 350);
  };

  const handleSelect = (item) => {
    onSelect(item.display, item.code, item.display);
    setSearchText("");
    setSuggestions([]);
    setShowDropdown(false);
    setNoResults(false);
    setActiveIndex(-1);
  };

  const handleClear = () => {
    onClear();
    setSearchText("");
    if (onChange) {
      onChange("");
    }
    setSuggestions([]);
    setShowDropdown(false);
    setNoResults(false);
    setActiveIndex(-1);
    // Focus the search input after clearing
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  // Selected state — show read-only display with clear button
  if (isSelected) {
    const containerStyle = compact
      ? { ...dropdownStyles.selectedContainer, height: "31px", padding: "0 6px", fontSize: "13px" }
      : dropdownStyles.selectedContainer;
    const displayLabel = snomedDisplay || drugName || snomedCode;
    return (
      <div style={containerStyle}>
        <div style={dropdownStyles.selectedText} title={displayLabel}>
          {displayLabel}
        </div>
        {snomedCode && (
          <span style={dropdownStyles.snomedBadge}>{snomedCode}</span>
        )}
        {!disabled && (
          <button
            type="button"
            style={dropdownStyles.clearBtn}
            onClick={handleClear}
            title="Clear selection and search again"
            aria-label="Clear medicine selection"
          >
            ✕
          </button>
        )}
      </div>
    );
  }

  // Search state — show text input with dropdown
  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        type="text"
        disabled={disabled}
        style={disabled ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" } : undefined}
        className={compact ? "form-control form-control-sm" : undefined}
        placeholder={placeholder || "Search for Medication..."}
        value={searchText}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0 || noResults) setShowDropdown(true);
        }}
        autoComplete="off"
      />
      {showDropdown && (
        <div style={dropdownStyles.container} role="listbox">
          {isLoading && (
            <div style={dropdownStyles.message}>
              <span
                style={{
                  display: "inline-block",
                  width: "14px",
                  height: "14px",
                  border: "2px solid #d1d5db",
                  borderTopColor: "#4b72b8",
                  borderRadius: "50%",
                  animation: "medicine-ac-spin 0.6s linear infinite",
                  marginRight: "8px",
                  verticalAlign: "middle",
                }}
              />
              Searching...
            </div>
          )}
          {!isLoading && noResults && (
            <div style={dropdownStyles.message}>No matches found</div>
          )}
          {!isLoading &&
            suggestions.map((item, idx) => (
              <div
                key={item.code}
                role="option"
                aria-selected={idx === activeIndex}
                onClick={() => handleSelect(item)}
                style={{
                  ...dropdownStyles.item,
                  backgroundColor: idx === activeIndex ? "#f0f7ff" : "#fff",
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseLeave={() => setActiveIndex(-1)}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.display}
                </span>
                <span style={dropdownStyles.code}>{item.code}</span>
              </div>
            ))}
        </div>
      )}
      <style>{`
        @keyframes medicine-ac-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MedicineAutocomplete;
